import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import {
  todayISO,
  parseISO,
  MONTH_NAMES_ID,
  calendarYearPath,
  calendarMonthPath,
  calendarDatePath,
} from "@/lib/calendar/dates";
import { useLocale } from "@/i18n/LocaleContext";
import { withSlash, absUrl } from "@/lib/url";
import { SUPPORTED_YEARS } from "@/data/holidays";
import { SourcesNote } from "@/components/SourcesNote";
import { currentHijriYear } from "@/lib/calendar/hijri";

const CANONICAL = "https://hari.co.id/kalender/";

export const Route = createFileRoute("/kalender/")({
  head: () => ({
    meta: [
      { title: "Kalender Indonesia - Tahun, Bulan & Tanggal Lengkap" },
      {
        name: "description",
        content:
          "Hub kalender Indonesia: pilih tahun, bulan, atau tanggal untuk melihat tanggal merah, libur nasional, cuti bersama, weton Jawa, dan Hijriah.",
      },
      { property: "og:title", content: "Kalender Indonesia - Tahun, Bulan & Tanggal" },
      { property: "og:description", content: "Pilih tahun, bulan, atau tanggal untuk kalender Indonesia lengkap." },
      { property: "og:url", content: CANONICAL },
      { property: "og:locale", content: "id_ID" },
      { property: "og:locale:alternate", content: "en_US" },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
  }),
  component: KalenderIndexPage,
});

function KalenderIndexPage() {
  return (
    <SiteShell>
      <Body />
    </SiteShell>
  );
}

function Body() {
  const { href } = useLocale();
  const today = parseISO(todayISO())!;
  const cy = today.y;
  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const ld = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Beranda", item: absUrl(href("/")) },
      { "@type": "ListItem", position: 2, name: "Kalender", item: absUrl(withSlash(href("/kalender"))) },
    ],
  };
  const collectionLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "Kalender Indonesia",
    inLanguage: "id-ID",
    isPartOf: { "@type": "WebSite", name: "Hari.co.id", url: "https://hari.co.id/" },
    about: [
      { "@type": "DefinedTerm", name: "Kalender Masehi", sameAs: ["https://id.wikipedia.org/wiki/Kalender_Gregorius"] },
      { "@type": "DefinedTerm", name: "Kalender Hijriah", sameAs: ["https://id.wikipedia.org/wiki/Kalender_Hijriah"] },
      { "@type": "DefinedTerm", name: "Kalender Jawa", sameAs: ["https://id.wikipedia.org/wiki/Kalender_Jawa"] },
      { "@type": "Thing", name: "Hari Libur Nasional Indonesia", sameAs: ["https://id.wikipedia.org/wiki/Hari_libur_di_Indonesia"] },
    ],
    mainEntity: {
      "@type": "ItemList",
      itemListElement: Array.from({ length: 17 }, (_, i) => cy - 8 + i).map((y, i) => ({
        "@type": "ListItem", position: i + 1, name: `Kalender ${y}`, url: `https://hari.co.id/kalender/${y}/`,
      })),
    },
  };
  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(ld) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(collectionLd) }} />
      <nav className="py-4 text-sm text-muted-foreground">
        <Link to={href("/") as never} className="hover:text-primary">Beranda</Link> / Kalender
      </nav>
      <header className="mb-6">
        <h1 className="font-display text-4xl tracking-tight md:text-5xl">Kalender Indonesia</h1>
        <p className="mt-2 text-muted-foreground">
          Pilih tahun, bulan, atau tanggal untuk kalender Indonesia lengkap dengan tanggal merah, libur nasional, cuti bersama, weton Jawa, dan Hijriah.
        </p>
      </header>

      <section className="mb-8">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">Hari ini & cepat</h2>
        <div className="flex flex-wrap gap-2 text-sm">
          <Link to={href(calendarDatePath(today.y, today.m, today.d)) as never} className="rounded-md bg-primary/10 px-3 py-1.5 font-medium text-primary hover:bg-primary/15">
            Hari ini, {today.d} {MONTH_NAMES_ID[today.m - 1]} {today.y}
          </Link>
          <Link to={href(calendarMonthPath(today.y, today.m)) as never} className="rounded-md bg-muted px-3 py-1.5 hover:bg-accent">
            Bulan ini ({MONTH_NAMES_ID[today.m - 1]} {today.y})
          </Link>
          <Link to={href(calendarYearPath(today.y)) as never} className="rounded-md bg-muted px-3 py-1.5 hover:bg-accent">
            Tahun ini ({today.y})
          </Link>
        </div>
      </section>

      <section className="mb-8">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">Bulan {cy}</h2>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
          {months.map((m) => (
            <Link key={m} to={href(calendarMonthPath(cy, m)) as never}
              className={"rounded-md border border-border bg-card px-3 py-2 text-sm hover:bg-accent " + (m === today.m ? "ring-1 ring-primary text-primary" : "")}>
              {MONTH_NAMES_ID[m - 1]} {cy}
            </Link>
          ))}
        </div>
      </section>

      <section className="mb-8">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">Tahun</h2>
        <div className="flex flex-wrap gap-2">
          {SUPPORTED_YEARS.map((y) => (
            <Link key={y} to={href(calendarYearPath(y)) as never}
              className={"rounded-md border border-border px-3 py-1.5 text-sm hover:bg-accent " + (y === cy ? "bg-primary/10 font-semibold text-primary" : "")}>
              {y}
            </Link>
          ))}
        </div>
      </section>

      <section className="mb-8">
        <details className="rounded-lg border border-border bg-card">
          <summary className="cursor-pointer px-4 py-2 text-sm font-medium">
            Pilih tahun lain (±50 tahun dari sekarang)
          </summary>
          <div className="border-t border-border p-3">
            <div className="grid grid-cols-5 gap-1.5 sm:grid-cols-10">
              {Array.from({ length: 101 }, (_, i) => cy - 50 + i).map((y) => (
                <Link
                  key={y}
                  to={href(calendarYearPath(y)) as never}
                  className={
                    "rounded border border-border px-2 py-1 text-center text-xs hover:bg-accent " +
                    (y === cy ? "bg-primary/10 font-semibold text-primary" : "")
                  }
                >
                  {y}
                </Link>
              ))}
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              Detail libur nasional & cuti bersama hanya tersedia untuk rentang resmi SKB. Tahun di luar itu menampilkan struktur kalender + tanggal Hijriah/Jawa.
            </p>
          </div>
        </details>
      </section>

      <section className="mb-8 rounded-lg border border-border bg-card p-5">
        <h2 className="text-lg font-semibold">Kalender Hijriah</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Lihat juga <Link to={href(`/kalender-hijriah/${currentHijriYear()}/`) as never} className="text-primary hover:underline">Kalender Hijriah {currentHijriYear()} H</Link> untuk konversi Masehi-Hijriah, hari besar Islam, Ramadhan, dan Idul Fitri.
        </p>
      </section>

      <section className="mb-8 rounded-lg border border-border bg-card p-5 text-sm">
        <h2 className="text-lg font-semibold">Sumber Data</h2>
        <p className="mt-2 text-muted-foreground">
          Data libur nasional dan cuti bersama dirujuk dari Surat Keputusan Bersama (SKB) 3 Menteri:
          Menteri Agama, Menteri Ketenagakerjaan, dan Menteri PAN-RB Republik Indonesia. Hari penting nasional
          dirujuk dari Kementerian terkait dan publikasi resmi pemerintah. Konversi Hijriah memakai algoritma
          aritmetika sipil (Kuwaiti). Untuk keputusan resmi awal Ramadhan/Idul Fitri ikuti sidang isbat Kemenag.
        </p>
        <div className="mt-3">
          <SourcesNote variant="kalender" />
        </div>
      </section>
    </>
  );
}